import { useLocation } from 'react-router';
import type { HeaderCarousel } from 'types/capi/modules';
import type {
  ExternalLink as ExternalLinkType,
  Image as ImageType,
  InternalLink as InternalLinkType
} from 'types/capi/shared';

import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import SplashSlideInteractionEvent, {
  type SplashSlideModuleType
} from '~/events/SplashSlideInteractionEvent';
import useEnv from '~/hooks/useEnv';
import useImpressions from '~/hooks/useImpressions';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useSplashSlideImpression from '~/hooks/useSplashSlideImpression';
import useUser from '~/hooks/useUser';
import AffiliateLogo from '~/routes/pdps/components/WhereToBuy/AffiliateLogo';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import { Analytics, Variant } from '.';

type SlideItem = HeaderCarousel['items'][0];

type Props = {
  analytics: Analytics;
  index: number;
  isFullBleed?: boolean;
  modulePosition?: number;
  moduleType: SplashSlideModuleType;
  slide: SlideItem;
  variant: Variant;
};

export default function Slide({
  isFullBleed = true,
  slide,
  index,
  modulePosition,
  moduleType,
  variant,
  analytics
}: Props) {
  const slideRef = useImpressions([
    {
      bannerId: `${analytics.id}_${index + 1}`,
      bannerName: `${analytics.id}_${index + 1}_${slide.heading?.replace(/ /g, '-')}`,
      bannerPosition: analytics.position
    }
  ]);

  const impressionRef = useSplashSlideImpression<HTMLDivElement>({
    modulePosition,
    moduleType,
    position: index + 1,
    slide
  });

  const isWholeSlideClickable = !!slide.cta && !slide.cta.text;

  return (
    <div
      className={`relative inline-block min-w-full`}
      ref={(element) => {
        slideRef[0]?.(element);
        impressionRef.current = element;
      }}
    >
      <SplashImage
        size={variant}
        mobile={slide.images.mobile}
        desktop={slide.images.desktop}
      />
      <div
        className={`content-width absolute inset-0 z-10 flex select-none items-end justify-start py-4 pb-8 md:items-center md:py-0 ${
          isFullBleed ? 'size-full' : 'desktop:px-8'
        } ${slide?.colour === 'white' ? 'text-white' : 'text-black'}`}
      >
        {slide.cta && !isWholeSlideClickable ? (
          <CTA
            analytics={analytics}
            name={`${analytics.id}_${index}_${slide.heading?.replace(' ', '-')}`}
            className="flex h-auto w-full flex-col gap-2 hover:cursor-pointer"
            cta={slide.cta}
            modulePosition={modulePosition}
            moduleType={moduleType}
            position={index + 1}
            slide={slide}
          >
            <Logo logo={slide.logo} />
            <TextBlock
              heading={slide.heading}
              subHeading={slide.sub_heading}
              variant={variant}
              colour={slide.colour}
              ctaText={slide.cta.text}
            />
          </CTA>
        ) : (
          <div className="flex h-auto w-full flex-col gap-4 md:gap-6">
            {isWholeSlideClickable && <Logo logo={slide.logo} />}
            <TextBlock
              variant={variant}
              heading={slide.heading}
              subHeading={slide.sub_heading}
              colour={slide.colour}
            />
          </div>
        )}
      </div>
      {isWholeSlideClickable && slide.cta && (
        <CTA
          analytics={analytics}
          ariaLabel={
            slide.heading || slide.sub_heading
              ? undefined
              : slide.images.desktop.alt || slide.images.mobile.alt
          }
          name={`${analytics.id}_${index + 1}`}
          className="absolute inset-0 z-20"
          cta={slide.cta}
          modulePosition={modulePosition}
          moduleType={moduleType}
          position={index + 1}
          slide={slide}
        />
      )}
    </div>
  );
}

function Logo({ logo }: { logo: SlideItem['logo'] }) {
  if (!logo) return null;

  return (
    <div className="flex flex-row items-center justify-start gap-2 lg:gap-4">
      {logo.image && (
        <div className="flex size-8 items-center justify-center rounded-lg lg:size-10">
          <AffiliateLogo src={logo.image.url} alt={logo.image.alt} />
        </div>
      )}
      {logo.text && <span className="text-base">{logo.text}</span>}
    </div>
  );
}

type TextBlockProps = {
  colour: 'black' | 'white';
  ctaText?: string;
  heading?: string;
  subHeading?: string;
  variant: Variant;
};

function TextBlock({
  subHeading,
  colour,
  heading,
  ctaText,
  variant
}: TextBlockProps) {
  if (!heading && !subHeading && !ctaText) return;

  let className = {
    headingFont: '',
    subHeadingFont: ''
  };

  switch (variant) {
    case 'base':
      className = {
        headingFont: 'text-hero',
        subHeadingFont: 'lg:text-base text-sm lg:leading-6 leading-[22px]'
      };
      break;
    case 'small-lg-font':
      className = {
        headingFont: 'text-display',
        subHeadingFont: 'text-base lg:leading-6 leading-[22px]'
      };
      break;
    case 'small-md-font':
      className = {
        headingFont: 'text-display',
        subHeadingFont: 'text-base lg:leading-6 leading-[22px]'
      };
      break;
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <div className="flex w-full flex-col gap-1 md:max-w-[75%] lg:gap-2">
        {heading && <h1 className={`${className.headingFont}`}>{heading}</h1>}
        {subHeading && (
          <h2 className="text-base leading-[22px]">{subHeading}</h2>
        )}
      </div>
      {ctaText && (
        <span
          className={`w-fit min-w-[150px] rounded-lg border px-4 py-3.5 text-center font-heading font-semibold leading-none transition duration-200 ease-in-out active:scale-95 md:min-w-[200px] ${colour === 'white' ? 'border-white' : 'border-black'}`}
        >
          {ctaText}
        </span>
      )}
    </div>
  );
}

// `position` is the slide's authored ordinal, not the snap the carousel was showing: it loops
// and auto-advances, so the visible index says nothing stable about which slide this was.
function CTA({
  cta,
  className,
  analytics,
  ariaLabel,
  children,
  modulePosition,
  moduleType,
  name,
  position,
  slide
}: {
  analytics: Analytics;
  ariaLabel?: string;
  children?: React.ReactNode;
  className: string;
  cta: ExternalLinkType | InternalLinkType;
  modulePosition?: number;
  moduleType: SplashSlideModuleType;
  name: TrackedButtonClickName;
  position: number;
  slide: SlideItem;
}) {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const slideEvent = {
    guestId: user.guestId,
    heading: slide.heading,
    imageUrl: slide.images.mobile?.url || slide.images.desktop.url,
    isBot,
    modulePosition,
    moduleType,
    position
  };

  if (cta.type === 'internal') {
    return (
      <Tracked.InternalLink
        aria-label={ariaLabel}
        name={name}
        pagePosition={analytics.position}
        variant="none"
        className={className}
        to={cta.path}
        onClick={() =>
          new SplashSlideInteractionEvent({
            ...slideEvent,
            destinationPath: cta.path,
            gesture: 'click'
          }).send()
        }
      >
        {children}
      </Tracked.InternalLink>
    );
  }

  if (cta.type === 'external') {
    const { url, clickRef } = getTrackedUrlAndClickRef(cta, { user });

    const onClick = () => {
      new SplashSlideInteractionEvent({
        ...slideEvent,
        clickref: clickRef,
        destinationUrl: cta.url,
        gesture: 'click',
        retailerId: cta.affiliate?.canonical_id
      }).send();

      wct.fireEvent({
        clickRef,
        clientBrowser,
        clientIp,
        landingPage: marketingCookies.getLandingPage() ?? 'unknown',
        pagePath: `${env.SITE_BASE_URL}${pathname}${search}`,
        session,
        url
      });

      if (cta.affiliate)
        new AffiliateLinkClickEvent({
          affiliateName: cta.affiliate.name,
          clientIp,
          isBot,
          isReseller: cta.affiliate.is_reseller,
          pagePosition: analytics.position,
          retailerId: cta.affiliate.id,
          user
        }).send();
    };

    return (
      <Tracked.ExternalLink
        aria-label={ariaLabel}
        variant="none"
        className={className}
        target="_blank"
        href={url}
        name={name}
        pagePosition={analytics.position}
        onClick={onClick}
      >
        {children}
      </Tracked.ExternalLink>
    );
  }

  return null;
}

function SplashImage({
  size,
  desktop,
  mobile
}: {
  desktop: ImageType;
  mobile: ImageType;
  size: Variant;
}) {
  let heightClass;

  switch (size) {
    case 'base':
      heightClass = 'h-[112.5vw] md:h-[502px]';
      break;
    case 'small-lg-font':
    case 'small-md-font':
      heightClass = 'h-[325px] md:h-[375px]';
      break;
  }

  return (
    <>
      <Image
        alt={mobile.alt}
        src={mobile.url}
        display="full-bleed"
        className={`w-full object-cover ${heightClass}`}
        responsiveOptions={{
          md: {
            src: desktop.url
          },
          xs: {
            src: mobile.url
          }
        }}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
      />
    </>
  );
}
